import React, { useEffect, useState } from 'react';

import { Flex, Loader, Text } from '@mantine/core';
import classNames from 'classnames';

import EditIcon from '@/assets/icons/EditIcon';
import { SuccessOpenIcon } from '@/assets/icons/SuccessOpenIcon';

import LabelText from '../LabelText';
import TextInputWrapper from '../TextInputWrapper';

import classes from './LabeledField.module.scss';

export interface LabeledFieldProps {
  label: string;
  value: string | React.ReactNode;
  labelIcon?: React.ReactNode;
  isEditable?: boolean;
  onEdit?: () => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  onSubmit?: (value: string) => void;
  customEditableComponent?: React.ReactNode;
  className?: string;
  isLoading?: boolean;
  labelClassName?: string;
  valueClassName?: string;
  labelVariant?: 'allCaps' | 'regular';
  showEditIcon?: boolean;
}

const LabeledField: React.FC<LabeledFieldProps> = ({
  label,
  value,
  labelIcon,
  isEditable = false,
  onEdit,
  onChange,
  onBlur,
  onSubmit,
  customEditableComponent,
  className,
  labelVariant = 'allCaps',
  labelClassName,
  valueClassName,
  isLoading = false,
  showEditIcon = false
}) => {
  const [inputValue, setInputValue] = useState(() => String(value));

  useEffect(() => {
    setInputValue(String(value));
  }, [value]);

  const handleActionClick = () => {
    if (isEditable && onSubmit) {
      onSubmit(String(inputValue).toLowerCase());
    } else if (onEdit) {
      onEdit();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (onChange) {
      onChange(e);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === 'Tab') && onSubmit) {
      e.preventDefault();
      onSubmit(String(inputValue).toLowerCase());
    }
  };

  const renderLabelIcon = () => {
    if (!labelIcon) return null;

    return (
      <span onClick={onEdit} className={classes.icon}>
        {labelIcon}
      </span>
    );
  };

  const renderActionIcon = () => {
    if ((!onEdit && !onSubmit) || !showEditIcon) {
      return null;
    }

    const getIcon = () => {
      if (isLoading) {
        return <Loader size={12} />;
      }
      if (isEditable) {
        return <SuccessOpenIcon />;
      }

      return <EditIcon />;
    };

    return (
      <span onClick={handleActionClick} className={classes.icon}>
        {getIcon()}
      </span>
    );
  };

  const renderInput = () => {
    if (customEditableComponent) {
      return customEditableComponent;
    }

    return (
      <TextInputWrapper
        type="text"
        className={classNames(classes.input, valueClassName)}
        value={inputValue}
        onChange={handleInputChange}
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
      />
    );
  };

  const renderDisplayValue = () => {
    return (
      <Text fz={14} fw={500} className={classNames(classes.value, valueClassName)}>
        {value}
      </Text>
    );
  };

  const renderContent = () => {
    if (isEditable) {
      return renderInput();
    }

    return renderDisplayValue();
  };

  return (
    <div className={className}>
      <Flex align="flex-start" gap={4}>
        <LabelText label={label} className={labelClassName} variant={labelVariant} />
        {renderLabelIcon()}
        {renderActionIcon()}
      </Flex>
      {renderContent()}
    </div>
  );
};

export default LabeledField;
